/**
 * 音频分析测试音
 * ------------------------------------------------------------
 * 仅用于验证 Web Audio 分析链路、光效参数和「歌词—音频同一时间线」的协调。
 * 这些声音由程序即时合成（简单的持续音 + 每拍音头），不是念佛录音，
 * 对应的时间轴 status = 'test'，也不是任何真实音频的对齐结果。
 */
import type { Playlist, TestToneSpec, Timeline, TimelineLine, Track } from './types';

const SR = 22050;
const LEAD_IN = 1.6;

interface PatternInfo {
  text: string;
  syllables: number;
  beat: number;
  restBeats: number;
  /** 每个音节的音高（半音，相对 A3=220Hz） */
  melody: number[];
}

const PATTERNS: Record<'six' | 'four', PatternInfo> = {
  six: { text: '南无阿弥陀佛', syllables: 6, beat: 0.52, restBeats: 1, melody: [0, 0, 3, 5, 3, 0] },
  four: { text: '阿弥陀佛', syllables: 4, beat: 0.74, restBeats: 1, melody: [3, 5, 3, 0] },
};

const SWEEP_SEGMENTS = [
  { text: '测试 · 低频段 30–250 Hz', f: 90, kind: 'tone' as const },
  { text: '测试 · 中低频 250–1000 Hz', f: 500, kind: 'tone' as const },
  { text: '测试 · 中高频 1–4 kHz', f: 2200, kind: 'tone' as const },
  { text: '测试 · 高频段 4–12 kHz', f: 7000, kind: 'high' as const },
  { text: '测试 · 全频段', f: 0, kind: 'all' as const },
];
const SWEEP_SEG = 6;

export const TEST_PLAYLIST_ID = 'debug-test-tones';

export const TEST_TRACKS: Track[] = [
  {
    id: 'test-six',
    title: '【测试音】六字节拍 · 非正式曲目',
    artist: '程序合成 · 仅供调试光效',
    kind: 'test-tone',
    src: null,
    scene: 'jindian',
    testTone: { pattern: 'six', duration: 62 },
  },
  {
    id: 'test-four',
    title: '【测试音】四字节拍 · 非正式曲目',
    artist: '程序合成 · 仅供调试光效',
    kind: 'test-tone',
    src: null,
    scene: 'lianchi',
    testTone: { pattern: 'four', duration: 60 },
  },
  {
    id: 'test-sweep',
    title: '【测试音】频段扫描 · 非正式曲目',
    artist: '程序合成 · 仅供调试频段权重',
    kind: 'test-tone',
    src: null,
    scene: 'liuli',
    testTone: { pattern: 'sweep', duration: SWEEP_SEGMENTS.length * SWEEP_SEG + LEAD_IN },
  },
];

export const TEST_PLAYLIST: Playlist = {
  id: TEST_PLAYLIST_ID,
  title: '调试 · 音频分析测试音',
  subtitle: '程序合成，非念佛录音，仅用于调试',
  kind: 'debug',
  tracks: TEST_TRACKS.map((t) => t.id),
};

/** 生成与测试音严格对应的测试时间轴（status = test） */
export function testTimeline(spec: TestToneSpec, trackId: string): Timeline {
  const lines: TimelineLine[] = [];
  if (spec.pattern === 'sweep') {
    SWEEP_SEGMENTS.forEach((s, i) => {
      const start = LEAD_IN + i * SWEEP_SEG;
      lines.push({ start, end: start + SWEEP_SEG - 0.3, text: s.text });
    });
  } else {
    const p = PATTERNS[spec.pattern];
    const lineDur = (p.syllables + p.restBeats) * p.beat;
    for (let t = LEAD_IN, i = 0; t + lineDur <= spec.duration; t += lineDur, i++) {
      lines.push({ start: round3(t), end: round3(t + p.syllables * p.beat + 0.12), text: p.text });
    }
  }
  return {
    format: 'nianfo-timeline',
    version: 1,
    trackId,
    offset: 0,
    status: 'test',
    note: '测试音专用时间轴，由合成参数直接计算，不是真实音频的对齐结果',
    lines,
  };
}

const cache = new Map<string, string>();

/** 合成测试音并返回 blob: URL（结果会缓存） */
export function testToneUrl(spec: TestToneSpec): string {
  const key = `${spec.pattern}:${spec.duration}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const samples = spec.pattern === 'sweep' ? synthSweep(spec.duration) : synthChantPulse(PATTERNS[spec.pattern], spec.duration);
  const url = URL.createObjectURL(encodeWav(samples, SR));
  cache.set(key, url);
  return url;
}

function synthChantPulse(p: PatternInfo, duration: number): Float32Array {
  const n = Math.floor(duration * SR);
  const out = new Float32Array(n);
  const lineDur = (p.syllables + p.restBeats) * p.beat;
  let seed = 1;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647) * 2 - 1;

  for (let i = 0; i < n; i++) {
    const t = i / SR;
    // 持续低音（呼吸式起伏）
    const breath = 0.6 + 0.4 * Math.sin(t * 0.35);
    let s = 0.05 * breath * (Math.sin(2 * Math.PI * 110 * t) + 0.5 * Math.sin(2 * Math.PI * 165 * t) + 0.25 * Math.sin(2 * Math.PI * 220.5 * t));

    if (t >= LEAD_IN) {
      const lt = t - LEAD_IN;
      const lineIdx = Math.floor(lt / lineDur);
      const inLine = lt - lineIdx * lineDur;
      const syl = Math.floor(inLine / p.beat);
      if (syl < p.syllables && LEAD_IN + (lineIdx + 1) * lineDur <= duration + 1e-6) {
        const st = inLine - syl * p.beat;
        // 句与句之间的强弱变化，便于观察响度包络
        const phrase = 0.55 + 0.45 * Math.sin(lineIdx * 0.9);
        const accent = syl === 0 ? 1.25 : 1;
        const env = Math.min(1, st / 0.03) * Math.exp(-st * 2.2) * phrase * accent;
        const f0 = 220 * Math.pow(2, p.melody[syl] / 12) * (1 + 0.004 * Math.sin(2 * Math.PI * 5.2 * t));
        let voice = 0;
        for (let k = 1; k <= 6; k++) voice += Math.sin(2 * Math.PI * f0 * k * t) * (k === 2 || k === 3 ? 0.6 : 0.35 / k);
        s += 0.16 * env * voice;
        // 音头：衰减很快的高音，形成明显的起音
        const click = Math.exp(-st * 60) * phrase;
        s += 0.22 * click * Math.sin(2 * Math.PI * 920 * t) + 0.05 * click * rand();
      }
    }
    out[i] = s;
  }
  fadeEdges(out);
  return out;
}

function synthSweep(duration: number): Float32Array {
  const n = Math.floor(duration * SR);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / SR;
    if (t < LEAD_IN) continue;
    const seg = Math.floor((t - LEAD_IN) / SWEEP_SEG);
    const s = SWEEP_SEGMENTS[seg];
    if (!s) continue;
    const st = t - LEAD_IN - seg * SWEEP_SEG;
    // 每 0.5 秒一个脉冲，叠加缓慢渐强
    const pulse = Math.exp(-((st % 0.5) * 7));
    const ramp = Math.min(1, st / 1.5) * Math.min(1, (SWEEP_SEG - st) / 0.4);
    const amp = (0.25 + 0.75 * pulse) * ramp;
    const sin = (f: number) => Math.sin(2 * Math.PI * f * t);
    let v = 0;
    if (s.kind === 'tone') v = sin(s.f) * 0.4;
    // 高频段：若干 4.5–9.5 kHz 的正弦叠加（采样率 22050 Hz，奈奎斯特 11 kHz）
    else if (s.kind === 'high') v = 0.12 * (sin(4700) + sin(6100) + sin(7300) + sin(8900));
    else v = 0.14 * (sin(90) + sin(500) + sin(2200) + sin(6100));
    out[i] = v * amp;
  }
  fadeEdges(out);
  return out;
}

function fadeEdges(a: Float32Array): void {
  const f = Math.min(a.length / 2, SR * 0.05);
  for (let i = 0; i < f; i++) {
    a[i] *= i / f;
    a[a.length - 1 - i] *= i / f;
  }
}

function encodeWav(samples: Float32Array, sr: number): Blob {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const w = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sr, true);
  v.setUint32(28, sr * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  w(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
